// src/pages/Marketplace/shared/weekHelpers.ts

/**
 * Spotlight weeks are plain `YYYY-MM-DD` Mondays, not instants, and the backend
 * derives "current week" in UTC on purpose (the rotation cron fires 00:00 UTC
 * Monday, which leads every US market). So every helper here works in UTC and
 * never touches the market timezone — unlike surge events, a Spotlight week has
 * no wall-clock component to localize, and localizing it would let the panel
 * disagree with the server about which week is still writable.
 */

const MS_PER_DAY = 86_400_000;

/** Parse a `YYYY-MM-DD` week key as UTC midnight. */
function parseWeekKey(weekStart: string): Date {
  return new Date(`${weekStart}T00:00:00Z`);
}

/** Format a UTC date back to a `YYYY-MM-DD` week key. */
function toWeekKey(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/** Monday of the UTC week containing `date` (defaults to now), as `YYYY-MM-DD`. */
export function utcWeekStart(date: Date = new Date()): string {
  // getUTCDay(): 0 = Sun … 6 = Sat. Shift so Monday is 0.
  const daysSinceMonday = (date.getUTCDay() + 6) % 7;
  const monday = new Date(date.getTime() - daysSinceMonday * MS_PER_DAY);
  return toWeekKey(new Date(Date.UTC(
    monday.getUTCFullYear(), monday.getUTCMonth(), monday.getUTCDate(),
  )));
}

/** Shift a week key by `weeks` (negative shifts back). */
export function addWeeks(weekStart: string, weeks: number): string {
  return toWeekKey(new Date(parseWeekKey(weekStart).getTime() + weeks * 7 * MS_PER_DAY));
}

/** True when a `YYYY-MM-DD` string parses and lands on a Monday in UTC. */
export function isMondayWeekKey(weekStart: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(weekStart)) return false;
  const parsed = parseWeekKey(weekStart);
  return !Number.isNaN(parsed.getTime()) && parsed.getUTCDay() === 1;
}

/**
 * `weeks` consecutive week keys starting at `first`, e.g. the panel's horizon.
 */
export function weekKeySequence(first: string, weeks: number): string[] {
  return Array.from({ length: Math.max(0, weeks) }, (_, i) => addWeeks(first, i));
}

/** "Aug 3 – Aug 9, 2026" — the Mon–Sun span a week key covers. */
export function formatWeekRange(weekStart: string): string {
  const monday = parseWeekKey(weekStart);
  const sunday = new Date(monday.getTime() + 6 * MS_PER_DAY);
  const md = new Intl.DateTimeFormat('en-US', {
    month: 'short', day: 'numeric', timeZone: 'UTC',
  });
  return `${md.format(monday)} – ${md.format(sunday)}, ${monday.getUTCFullYear()}`;
}

/** "Mon Aug 3, 2026" — a single week key, for pickers and confirmations. */
export function formatWeekKey(weekStart: string): string {
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC',
  }).format(parseWeekKey(weekStart));
}
